<template>
  <div class="inner">
    <div class="flex justify-between items-center">
      <h1>AM Best Ratings</h1>
      <label class="input input-sm flex min-w-96 items-center gap-2 place-self-end self-center">
        <input
          type="text"
          placeholder="Search"
          class="grow"
          v-model="searchText"
        />
        <XIcon
          v-if="searchText.length > 0"
          :size="20"
          @click="searchText = ''"
        />
        <SearchIcon :size="20" />
      </label>
    </div>
    <div class="flex justify-between items-center">
      <FilterBar class="flex-1 items-center" v-if="!loading">
        <GenericMultiFilter
          :options="insurerGroupOptions"
          v-model="selectedInsurers"
          title="Carriers"
          chip-title-field="label"
          data-test="carriers-filter-btn"
          @update:model-value="applyFilters"
        />
        <GenericMultiFilter
          :options="carrierSizeOptions"
          v-model="selectedCarrierSizes"
          title="Carrier Size"
          chip-title-field="label"
          data-test="carrier-size-filter-btn"
          @update:model-value="applyFilters"
        />
        <GenericMultiFilter
          :options="carrierStrengthOptions"
          v-model="selectedCarrierStrengths"
          title="Financial Strength"
          chip-title-field="label"
          data-test="carrier-strength-filter-btn"
          @update:model-value="applyFilters"
        />
        <template #filter-bar-right>
          <DownloadButton @click="downloadTable"/>
        </template>
      </FilterBar>

      <AppButton v-if="isAdmin" @click="showUploadModal = true">
        <Upload :size="16" class="stroke-primary" />
        Upload
      </AppButton>
    </div>

    <AppModal :show="showUploadModal" title="Upload AM Best Ratings">
      <template #content>
        <input type="file"
               class="file-input file-input-bordered border-solid w-full"
               :class="{ 'file-input-disabled': uploading, 'hidden': uploadSuccess }"
               ref="fileUpload"
               @change="uploadFileChanged"
        />

        <div v-if="uploadSuccess === false" class="mt-4">
          <div class="flex gap-1 pb-1">
            <AlertCircle class="text-error"/>
            <h3 class="text-red-500">Upload Failed</h3>
          </div>

          <ul v-if="uploadErrors.length > 0" class="list-disc list-inside text-red-500">
            <li v-for="error in uploadErrors" :key="error">{{ error }}</li>
          </ul>
          <p v-else class="pt-1">
            The file upload failed for an unspecified reason. Ensure the file is UTF-8 encoded.
            Special characters may cause an error while processing.
          </p>
        </div>

        <div v-else-if="uploadSuccess === true" class="mt-4">
          <div class="flex gap-1 pb-1">
            <CheckCircle class="text-success"/>
            <h3 class="text-green-500">Upload Successful</h3>
          </div>
        </div>

        <div v-if="uploading" class="mt-4 flex gap-1">
          <span class="loading loading-spinner loading-md"></span>
          <h3>Uploading...</h3>
        </div>
      </template>

      <template #actions>
        <template v-if="uploadSuccess">
          <button class="btn btn-sm btn-primary" @click="dismissUpload">Close</button>
        </template>
        <template v-else>
          <button class="btn btn-sm" @click="dismissUpload">Cancel</button>
          <button class="btn btn-sm btn-primary text-white"
                  :class="{ 'btn-disabled': !isFileSelectedForUpload } "
                  @click="confirmUpload"
          >
            Upload
          </button>
        </template>
      </template>

    </AppModal>

    <table v-tablesort class="dense">
      <thead>
      <tr>
        <td class="nosort">Issuing Company</td>
        <td class="nosort">AM Best Rating</td>
        <td class="nosort">Size Category</td>
        <td class="nosort">Rating Effective Date</td>
        <td class="nosort">Previous Rating</td>
      </tr>
      </thead>
      <tbody>
      <template v-for="group in groupedCarriers" :key="group.groupName">
        <tr >
          <td colspan="5" class="font-bold text-sm text-gray-500">{{ group.groupName }}</td>
        </tr>
        <tr v-for="carrier in group.carriers" :key="carrier.insurerGroup">
          <td class="pl-6"> {{ carrier.issuingCompanyName }}</td>
          <td class="pl-6"> {{ carrier.financialStrengthRating }}</td>
          <td class="pl-6"> {{ carrier.sizeCategory }}</td>
          <td class="pl-6"> {{ formatDate(carrier.ratingEffectiveDate) }}</td>
          <td class="pl-6"> {{ carrier.previousRating }}</td>
        </tr>
      </template>
      </tbody>
    </table>

  </div>
</template>

<script lang="ts">
import { carrierService } from '@/services/api';
import { CarrierRating, CarrierGroup } from '@/types/Carrier';
import { DownloadCSV } from '@/utilities/Download';
import FilterBar from '@/components/v2/FilterBar.vue';
import GenericMultiFilter from '@/components/v2/GenericMultiFilter.vue';
import { romanToArabic } from '@/utilities/Number';
import DownloadButton from '@/components/v2/DownloadButton.vue';
import { DateTime } from 'luxon';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { Upload, AlertCircle, CheckCircle, SearchIcon, XIcon } from '@lucide/vue';
import { IsUserAnAdmin } from '@/utilities/AuthUtils';
import store from '@/store';

interface GroupedCarriers {
  groupName: string;
  carriers: CarrierRating[]
}

export default {
  name: 'AMBestRatings',
  components: { Upload, AlertCircle, CheckCircle, SearchIcon, XIcon, AppButton, AppModal, FilterBar, GenericMultiFilter, DownloadButton },
  data: () => ({
    loading: true,
    ratings: [] as CarrierGroup[],
    carriers: [] as CarrierRating[],
    carrierSizes: [] as (string | undefined | null)[],
    carrierStrengthsTop: [] as (string | undefined | null)[],
    carrierStrengthsBottom: [] as (string | undefined | null)[],
    insurerGroups: [] as (string | undefined | null)[],
    searchText: '',
    appliedCarriers: [] as (string | undefined)[],
    selectedInsurers: [] as (string | number)[],
    selectedCarrierSizes: [] as (string | number)[],
    selectedCarrierStrengths: [] as (string | number)[],
    showUploadModal: false,
    uploadErrors: [] as string[],
    uploadSuccess: null as boolean | null,
    uploading: false,
    uploadFileName: '',
  }),
  methods: {
    async downloadTable() {
      const fieldList = ['Carrier Group', 'Issuing Company', 'AM Best Rating', 'Size Category', 'Rating Effective Date', 'Previous Rating'].join(',');
      const data = this.carriers
          .sort((c1, c2) => `${c1.insurerGroup}${c1.financialStrengthRating}`.localeCompare(`${c2.insurerGroup}${c2.financialStrengthRating}`))
          .map(c => `"${c.insurerGroup}","${c.issuingCompanyName}","${c.financialStrengthRating || ''}","${c.sizeCategory || ''}","${this.formatDate(c.ratingEffectiveDate)}","${c.previousRating || ''}"`).join('\n');
      DownloadCSV(`${fieldList}\n${data}`, 'AM Best Ratings');
    },
    async refreshData(): Promise<void> {
      this.ratings = await carrierService.amBestRatings();

      this.hydrateCarriers();

      this.carrierSizes = Array.from(new Set(this.ratings.flatMap((x: CarrierGroup) => (x.carriers.map(c => c.sizeCategory))).sort((a, b) => romanToArabic(a) - romanToArabic(b)).filter(x => x)));
      
      const strengthOrder = ['A++', 'A+',  'A', 'A-', 'A- u', 'B++', 'B+', 'B', 'B-', 'C++', 'C+', 'C', 'C-', 'D', 'E', 'F', 'NR', 'S'];
      let carrierStrengths = Array.from(new Set(this.ratings.flatMap((x: CarrierGroup) => (x.carriers.map(c => c.financialStrengthRating))).filter(x => x)));
      carrierStrengths = strengthOrder.filter(x => carrierStrengths.includes(x));
      //divide the carrier strengths into top and bottom halfs to be fed into the attribute filter component and display as intended
      this.carrierStrengthsTop = carrierStrengths.slice(0, Math.ceil(carrierStrengths.length / 2));
      this.carrierStrengthsBottom = carrierStrengths.slice(Math.ceil(carrierStrengths.length / 2));
      this.insurerGroups = Array.from(new Set(this.ratings.map(x => x.insurerGroup)));
      this.loading = false;
    },
    hydrateCarriers() {
      this.carriers = this.ratings.flatMap((group) => group.carriers);
    },
    applyFilters() {
      this.hydrateCarriers();
      if (this.selectedInsurers.length > 0) {
        this.carriers = this.carriers.filter(c => c.insurerGroup === 'All' || this.selectedInsurers.includes(c.insurerGroup));
      }
      if (this.selectedCarrierStrengths.length > 0) {
        this.carriers = this.carriers.filter(c => c.financialStrengthRating != null && this.selectedCarrierStrengths.includes(c.financialStrengthRating));
      }

      if (this.selectedCarrierSizes.length > 0) {
        this.carriers = this.carriers.filter(c => c.sizeCategory != null && c.sizeCategory !== '' && this.selectedCarrierSizes.includes(c.sizeCategory));
      }
    },
    formatDate(date: string | undefined): string {
      if (!date) return '';
      return DateTime.fromISO(date, { zone: 'utc' }).toLocaleString()
    },
    resetFileUpload() {
      this.uploadFileName = '';
      (this.$refs.fileUpload as HTMLInputElement).value = '';
      this.uploadSuccess = null;
      this.uploadFileName = '';
      this.uploadErrors = [];
    },
    dismissUpload() {
      if (this.uploadSuccess) {
        this.refreshData();
      }
      this.resetFileUpload();
      this.showUploadModal = false;
    },
    async confirmUpload() {
      const fileInput = this.$refs.fileUpload as HTMLInputElement;
      const file = fileInput.files?.[0];

      if (file) {
        this.uploadSuccess = null;
        this.uploadErrors = [];
        this.uploading = true;

        try {
          await carrierService.uploadAMBestRatings(file);
          this.uploadSuccess = true;
        } catch (e) {
          const err = e as Error;
          if (err.cause) {
            this.uploadErrors = (err.cause as { errors?: string[] }).errors ?? [];
          }
          this.uploadSuccess = false;
        }

        this.uploading = false;
      }

      fileInput.value = '';
    },
    uploadFileChanged(e: Event) {
      const fileInput = e.target as HTMLInputElement;
      this.uploadFileName = fileInput.value;
      this.uploadSuccess = null;
      this.uploadErrors = [];
    }
  },
  computed: {
    insurerGroupOptions() {
      return (this.insurerGroups.filter(Boolean) as string[]).map((x) => ({ label: x, value: x }));
    },
    carrierSizeOptions() {
      return (this.carrierSizes.filter(Boolean) as string[]).map((x) => ({ label: x, value: x }));
    },
    carrierStrengthOptions() {
      const strengths = [...(this.carrierStrengthsTop || []), ...(this.carrierStrengthsBottom || [])].filter(Boolean) as string[];
      return strengths.map((x) => ({ label: x, value: x }));
    },
    isAdmin(): boolean {
      return IsUserAnAdmin(store?.state.user);
    },
    isFileSelectedForUpload(): boolean {
      return this.uploadFileName.trim() !== '';
    },
    groupedCarriers(): GroupedCarriers[] {
      return this.filteredCarriers.reduce((acc, curr) => {
        const group = acc.find(g => g.groupName === curr.insurerGroup);
        if (group) {
          group.carriers.push(curr);
        } else {
          acc.push({ groupName: curr.insurerGroup, carriers: [curr] });
        }
        return acc;
      }, [] as GroupedCarriers[]);
    },
    filteredCarriers(): CarrierRating[] {
      return this.carriers.filter(c => c.issuingCompanyName.toLowerCase().includes(this.searchText.toLowerCase()));
    }
  },
  created(): Promise<void> {
    return this.refreshData();
  }
};
</script>

